/**
 * Minimal JWT claim extraction (no signature verification).
 *
 * We only inspect tokens we obtained ourselves from MSAL — they are already
 * trusted. The purpose is to read claims like `oid` (object ID) for
 * client-side filtering (e.g., owned vs shared flows).
 */

export interface JwtClaims {
  oid?: string;
  upn?: string;
  email?: string;
  preferred_username?: string;
  tid?: string;
  [key: string]: unknown;
}

/**
 * Decode a JWT's payload claims. Returns empty object on any failure.
 * Does NOT verify the signature — caller is responsible for trust.
 */
export function decodeJwtClaims(token: string): JwtClaims {
  try {
    const parts = token.split(".");
    if (parts.length < 2 || !parts[1]) return {};
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padding = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
    const json = Buffer.from(padded + padding, "base64").toString("utf-8");
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === "object" ? (claims as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Extract the current user's Azure AD object ID from an access token. */
export function getOidFromToken(token: string): string | undefined {
  const claims = decodeJwtClaims(token);
  const oid = claims.oid;
  return typeof oid === "string" && oid.length > 0 ? oid : undefined;
}
